import { useState, useEffect } from 'react';
import { Map as MapIcon, Globe, Music, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useStore } from '../store/useStore';
import { MapViewMapbox } from './MapViewMapbox';

// ─── Types ───────────────────────────────────────────────────────────
interface EmbedConfig {
  enabled?: boolean;
  visiblePoints?: string[];
  pointDescriptions?: Record<string, string>;
  lineUpTitle?: string;
  showLineUp?: boolean;
  primaryColor?: string;
  logoUrl?: string;
  title?: string;
  customCss?: string;
  headerBg?: string;
  pointColor?: string;
  stageColor?: string;
  lineupBg?: string;
  lineupCardBg?: string;
  lineupTextColor?: string;
  secondaryTitleColor?: string;
  lineupTitleColor?: string;
}

// ─── Main Component ──────────────────────────────────────────────────
interface EmbedMapViewProps { orgId: string; }

export function EmbedMapView({ orgId }: EmbedMapViewProps) {
  const [activeTab, setActiveTab] = useState<'map' | 'lineup'>('map');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig>({});
  const [storeReady, setStoreReady] = useState(false);

  // ── Fetch data & populate Zustand store ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);

        // Fetch everything in parallel
        const [orgRes, pointsRes, teamsRes, shiftsRes, hoursRes] = await Promise.all([
          supabase.from('organizations').select('*').eq('id', orgId).single(),
          supabase.from('points').select('*').eq('org_id', orgId),
          supabase.from('staff').select('*').eq('org_id', orgId).is('deleted_at', null),
          supabase.from('shifts').select('*').eq('org_id', orgId).is('deleted_at', null),
          supabase.from('point_opening_hours').select('*'),
        ]);

        if (cancelled) return;

        console.log('[EmbedMap] orgId:', orgId);
        console.log('[EmbedMap] orgRes error:', orgRes.error);
        console.log('[EmbedMap] pointsRes count:', (pointsRes.data || []).length, 'error:', pointsRes.error);
        console.log('[EmbedMap] teamsRes count:', (teamsRes.data || []).length, 'error:', teamsRes.error);
        console.log('[EmbedMap] shiftsRes count:', (shiftsRes.data || []).length, 'error:', shiftsRes.error);

        if (orgRes.error) {
          if (orgRes.error.code === 'PGRST116') setError('Organisation introuvable.');
          else setError(orgRes.error.message);
          setLoading(false);
          return;
        }

        const orgData = orgRes.data as any;
        const config = (orgData?.settings?.embedConfig || {}) as EmbedConfig;
        console.log('[EmbedMap] embedConfig:', config);

        setOrgName(orgData.name || '');
        setOrgLogo(orgData.logo_url || '');
        setEmbedConfig(config);

        if (!config.enabled) {
          setLoading(false);
          return;
        }

        // Map DB rows to store-compatible objects
        const rawPoints = (pointsRes.data || []) as any[];
        const rawTeams = (teamsRes.data || []) as any[];
        const rawShifts = (shiftsRes.data || []) as any[];
        const rawHours = (hoursRes.data || []) as any[];

        const mappedPoints = rawPoints.map((p: any) => {
          const pointHours = rawHours
            .filter((h: any) => h.point_id === p.id)
            .map((h: any) => ({
              start: (h.start_time || '').substring(0, 16),
              end: (h.end_time || '').substring(0, 16),
              requiredTeam: h.required_staff || 1,
            }));
          return {
            id: p.id,
            name: p.name,
            categoryId: p.category_id,
            orgId: p.org_id,
            projectId: p.project_id,
            requiredTeam: p.required_staff || 1,
            location: (p.location_lat != null && p.location_lng != null)
              ? { lat: p.location_lat, lng: p.location_lng } : undefined,
            iconName: p.icon_name,
            descriptionMarkdown: p.description_markdown,
            isStage: p.is_stage || false,
            isRestricted: p.is_restricted || false,
            isMapFocus: p.is_map_focus || false,
            isMealPoint: p.is_meal_point || false,
            swapEnabled: p.swap_enabled || false,
            unavailabilityEnabled: p.unavailability_enabled || false,
            openingHours: pointHours,
          };
        });

        const mappedTeams = rawTeams.map((s: any) => ({
          id: s.id,
          name: s.name,
          orgId: s.org_id,
          email: s.email || '',
          phone: s.phone || '',
          role: s.role || 'staff',
          roles: s.roles || [],
          isArtist: s.is_artist || false,
          isDistributor: s.is_distributor || false,
          categoryId: s.category_id,
          photo: s.photo_url,
          staffToken: s.staff_token,
          teamToken: s.team_token,
          accessPassword: s.access_password,
          isOnDuty: s.is_on_duty || false,
          specialties: s.specialties || [],
          linkedWalletQr: s.linked_wallet_qr,
          isProfileVisible: s.is_profile_visible || false,
          distAllowScan: s.dist_allow_scan || false,
          distAllowDirect: s.dist_allow_direct || false,
          distAllowCheckin: s.dist_allow_checkin || false,
          distAllowLinkWallet: s.dist_allow_link_wallet || false,
          distAllowView: s.dist_allow_view || false,
        }));

        const mappedShifts = rawShifts.map((sh: any) => ({
          id: sh.id,
          pointId: sh.point_id,
          teamId: sh.staff_id, // DB column is staff_id, not team_id
          orgId: sh.org_id,
          projectId: sh.project_id,
          start: (sh.start_time || sh.start || '').substring(0, 16),
          end: (sh.end_time || sh.end || '').substring(0, 16),
          shiftType: sh.shift_type || 'standard',
          taskId: sh.task_id,
          color: sh.color,
          actualStart: sh.actual_start,
          actualEnd: sh.actual_end,
        }));

        // Filter points to only those selected in embedConfig
        const visibleIds = new Set(config.visiblePoints || []);
        const filteredPoints = visibleIds.size > 0
          ? mappedPoints.filter((p: any) => visibleIds.has(p.id))
          : mappedPoints;

        // Find an artist team member for the GanttTimeline artist view context
        const anyArtist = mappedTeams.find((t: any) => t.isArtist);

        // Calculate best map focus: center on all visible points with locations
        const pointsWithLoc = filteredPoints.filter((p: any) => p.location);
        let mapFocus = undefined;
        if (pointsWithLoc.length > 0) {
          const avgLat = pointsWithLoc.reduce((s: number, p: any) => s + p.location.lat, 0) / pointsWithLoc.length;
          const avgLng = pointsWithLoc.reduce((s: number, p: any) => s + p.location.lng, 0) / pointsWithLoc.length;
          // Calculate appropriate zoom based on spread
          const lats = pointsWithLoc.map((p: any) => p.location.lat);
          const lngs = pointsWithLoc.map((p: any) => p.location.lng);
          const latSpread = Math.max(...lats) - Math.min(...lats);
          const lngSpread = Math.max(...lngs) - Math.min(...lngs);
          const maxSpread = Math.max(latSpread, lngSpread);
          let zoom = 15;
          if (maxSpread > 0.5) zoom = 10;
          else if (maxSpread > 0.1) zoom = 12;
          else if (maxSpread > 0.02) zoom = 14;
          else if (maxSpread > 0.005) zoom = 15;
          else if (maxSpread > 0.001) zoom = 16;
          else zoom = 17;
          mapFocus = { lat: avgLat, lng: avgLng, zoomLevel: zoom };
        }

        // Populate the Zustand store so existing components render identically
        useStore.setState({
          currentOrgId: orgId,
          organizations: [{
            id: orgId,
            name: orgData.name,
            adminToken: '',
            allowAdminSettings: false,
            settings: orgData.settings || { minRestTimeHours: 8 },
            createdAt: orgData.created_at || '',
            logoUrl: orgData.logo_url || '',
          }],
          points: filteredPoints,
          allPoints: filteredPoints,
          team: mappedTeams,
          shifts: mappedShifts,
          categories: [],
          tasks: [],
          issues: [],
          messages: [],
          currentUser: {
            role: 'admin', // admin = no point filtering on map, full visibility on timeline
            teamId: anyArtist?.id || undefined, // helps GanttTimeline identify artist context
            categoryId: undefined,
          } as any,
          sharedTeamOrganizations: [],
          impersonatedTeamId: null,
          viewAsManagerCategoryId: null,
          currentProjectId: null,
          mapFocus: mapFocus,
          setMapFocus: () => {}, // no-op for embed (read-only map)
        } as any);

        setStoreReady(true);
        setLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          console.error('[EmbedMap] Exception:', err);
          setError(err.message || 'Erreur de chargement.');
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Loading ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0A0B0E] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 text-indigo-400 animate-spin mx-auto" />
          <p className="text-slate-400 text-sm">Chargement de la carte...</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="fixed inset-0 bg-[#0A0B0E] flex items-center justify-center p-6">
        <div className="text-center max-w-md space-y-4">
          <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto" />
          <h2 className="text-white font-bold text-lg">{error}</h2>
          <p className="text-slate-400 text-xs">Vérifiez le lien ou contactez l'administrateur.</p>
        </div>
      </div>
    );
  }

  // ── Disabled ─────────────────────────────────────────────────────
  if (!embedConfig.enabled) {
    return (
      <div className="fixed inset-0 bg-[#0A0B0E] flex items-center justify-center p-6">
        <div className="text-center max-w-md space-y-4">
          <Globe className="w-10 h-10 text-slate-500 mx-auto" />
          <h2 className="text-white font-bold text-lg">Carte non disponible</h2>
          <p className="text-slate-400 text-xs">L'administrateur de <strong>{orgName}</strong> n'a pas activé la carte publique.</p>
        </div>
      </div>
    );
  }

  // ── Wait for store to be ready ───────────────────────────────────
  if (!storeReady) {
    return (
      <div className="fixed inset-0 bg-[#0A0B0E] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-indigo-400 animate-spin mx-auto" />
      </div>
    );
  }

  // ── Theme ───────────────────────────────────────────────────────
  const primaryColor = embedConfig.primaryColor || '#6366f1';
  const headerBg = embedConfig.headerBg || '#111319';
  const embedLogo = embedConfig.logoUrl || orgLogo;
  const embedTitle = embedConfig.title || orgName;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-[#0A0B0E] flex flex-col font-sans text-slate-100">
      {/* Custom CSS injection */}
      {embedConfig.customCss && <style dangerouslySetInnerHTML={{ __html: embedConfig.customCss }} />}
      {/* Header */}
      <div className="embed-header flex-none border-b border-[#1E222B] px-4 py-3 flex items-center justify-between" style={{ backgroundColor: headerBg }}>
        <div className="flex items-center gap-3">
          {embedLogo ? (
            <img src={embedLogo} alt={embedTitle} className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-sm" style={{ backgroundColor: primaryColor }}>
              {embedTitle.charAt(0)}
            </div>
          )}
          <div>
            <h1 className="text-sm font-black text-white">{embedTitle}</h1>
            <p className="text-[9px] text-slate-500 uppercase tracking-wider">Live Map</p>
          </div>
        </div>
        <span className="text-[8px] text-slate-600 uppercase tracking-widest">Powered by Bip Teams</span>
      </div>

      {/* Tabs */}
      <div className="flex-none flex border-b border-[#1E222B] px-4 gap-1" style={{ backgroundColor: headerBg }}>
        <button
          onClick={() => setActiveTab('map')}
          style={activeTab === 'map' ? { borderColor: primaryColor, color: '#fff' } : {}}
          className={`embed-tab embed-tab-map flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
            activeTab === 'map' ? 'text-white' : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          <MapIcon className="w-3.5 h-3.5" /> Map
        </button>
        {embedConfig.showLineUp !== false && (
          <button
            onClick={() => setActiveTab('lineup')}
            style={activeTab === 'lineup' ? { borderColor: primaryColor, color: '#fff' } : {}}
            className={`embed-tab embed-tab-lineup flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === 'lineup' ? 'text-white' : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            <Music className="w-3.5 h-3.5" /> {embedConfig.lineUpTitle || 'Line Up'}
          </button>
        )}
      </div>

      {/* Content — reuse existing components via populated store */}
      <div className="flex-1 relative overflow-hidden">
        {activeTab === 'map' ? (
          <MapViewMapbox isEmbedView embedPointColor={embedConfig.pointColor} embedStageColor={embedConfig.stageColor} />
        ) : (
          <EmbedLineUp
            primaryColor={primaryColor}
            bgColor={embedConfig.lineupBg || '#0A0B0E'}
            cardBg={embedConfig.lineupCardBg || '#14161B'}
            textColor={embedConfig.lineupTextColor || '#cbd5e1'}
            secondaryTitleColor={embedConfig.secondaryTitleColor || '#94a3b8'}
            lineupTitleColor={embedConfig.lineupTitleColor || embedConfig.primaryColor || '#6366f1'}
            lineUpTitle={embedConfig.lineUpTitle || 'Line Up'}
          />
        )}
      </div>
    </div>
  );
}

// ─── Embed Line Up Component ───────────────────────────────────────
// Shows only artist shifts on stage points — a clean schedule view
function EmbedLineUp({ primaryColor, bgColor, cardBg, textColor, secondaryTitleColor, lineupTitleColor, lineUpTitle }: {
  primaryColor: string;
  bgColor: string;
  cardBg: string;
  textColor: string;
  secondaryTitleColor: string;
  lineupTitleColor: string;
  lineUpTitle: string;
}) {
  const teams = useStore(s => s.team);
  const shifts = useStore(s => s.shifts);
  const points = useStore(s => s.points);
  const orgName = useStore(s => s.organizations[0]?.name || '');

  const now = new Date();
  const pointsMap = new Map(points.map(p => [p.id, p]));
  const teamsMap = new Map(teams.map(t => [t.id, t]));

  // Filter: only artists on stage points
  const stagePointIds = new Set(points.filter(p => p.isStage).map(p => p.id));
  const artistIds = new Set(teams.filter(t => t.isArtist).map(t => t.id));

  const lineupShifts = shifts
    .filter(s => {
      const isArtistShift = artistIds.has(s.teamId);
      const isOnStage = stagePointIds.has(s.pointId);
      return isArtistShift || isOnStage;
    })
    .filter(s => new Date(s.end) > now)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Group by date then by point
  const grouped: Record<string, Record<string, typeof shifts>> = {};
  lineupShifts.forEach(sh => {
    const dateKey = new Date(sh.start).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    if (!grouped[dateKey]) grouped[dateKey] = {};
    if (!grouped[dateKey][sh.pointId]) grouped[dateKey][sh.pointId] = [];
    grouped[dateKey][sh.pointId].push(sh);
  });

  if (lineupShifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-3">
          <Music className="w-10 h-10 text-slate-600 mx-auto" />
          <p className="text-slate-500 text-sm">Aucun artiste programmé pour le moment.</p>
          <p className="text-[10px] text-slate-600">Artistes: {teams.filter(t => t.isArtist).length} • Stages: {stagePointIds.size} • Shifts totaux: {shifts.length}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6" style={{ backgroundColor: bgColor }}>
      <div className="max-w-4xl mx-auto space-y-6">
        <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 sticky top-0 py-2 z-10" style={{ backgroundColor: bgColor, color: lineupTitleColor }}>
          <Music className="w-4 h-4" style={{ color: lineupTitleColor }} /> {lineUpTitle} • {orgName}
        </h2>
        {Object.entries(grouped).map(([dateKey, byPoint]) => (
          <div key={dateKey} className="space-y-4">
            <h3 className="text-xs font-black uppercase tracking-widest border-b border-[#1E222B] pb-2" style={{ color: secondaryTitleColor }}>
              {dateKey}
            </h3>
            {Object.entries(byPoint).map(([pointId, pointShifts]) => {
              const pt = pointsMap.get(pointId);
              return (
                <div key={pointId} className="space-y-2">
                  <h4 className="embed-lineup-stage text-[10px] font-black uppercase tracking-wider flex items-center gap-2 flex-wrap" style={{ color: secondaryTitleColor }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                    {pt?.name || 'Scène'}
                    {pt?.isStage && <span className="text-[7px] px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: primaryColor + '33', color: primaryColor }}>STAGE</span>}
                    {(pt as any)?.openingHours?.length > 0 && (
                      <span className="text-[8px] text-slate-500 font-normal normal-case ml-1">
                        ({(pt as any).openingHours.map((oh: any) => {
                          const s = oh.start?.split('T')[1]?.substring(0, 5) || oh.start;
                          const e = oh.end?.split('T')[1]?.substring(0, 5) || oh.end;
                          return s + '–' + e;
                        }).join(', ')})
                      </span>
                    )}
                  </h4>
                  <div className="space-y-1 pl-4">
                    {pointShifts.map((sh: any) => {
                      const tm = teamsMap.get(sh.teamId);
                      const start = new Date(sh.start);
                      const end = new Date(sh.end);
                      const timeStr = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        + ' — ' + end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                      return (
                        <div key={sh.id} className="embed-lineup-artist flex items-center gap-3 p-2.5 border border-[#2D3139]/50 rounded-lg transition-colors" style={{ backgroundColor: cardBg, borderColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0" style={{ backgroundColor: primaryColor + '33', border: '1px solid ' + primaryColor + '66', color: primaryColor }}>
                            {tm?.name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold truncate" style={{ color: textColor }}>{tm?.name || 'Inconnu'}</p>
                            {(tm?.roles as any)?.length > 0 && (
                              <p className="text-[8px] text-slate-500 truncate">{Array.isArray((tm as any).roles) ? (tm as any).roles.join(', ') : (tm as any).roles}</p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="embed-lineup-time text-[10px] font-mono font-bold" style={{ color: primaryColor }}>{timeStr}</p>
                            {tm?.isArtist && <span className="text-[7px] px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: primaryColor + '33', color: primaryColor }}>ARTIST</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
